import { activities as activitiesData, projects } from "../../../../../db";
import {
  calculateDurationInMonths,
  calculateProfessionalAttributedProjectDuration,
  normalizeSkillFormat,
  createSkillMaps,
  addSkillToMap,
  normalizeSkillMaps,
  type SkillMaps,
  type NormalizedSkillsByCategory
} from '../calculators/skillCalculationUtils';
import { fetchGitHubLanguages, isOpenSourceGitHubRepo } from './githubLanguages';

interface ProfessionalSkillsByCategory {
  profession: Array<{ name: string; level: number; }>;
  lead: Array<{ name: string; level: number; }>;
  language: Array<{ name: string; level: number; }>;
  tools: Array<{ name: string; level: number; }>;
}

interface EnhancedProject {
  id: number;
  title: string;
  categorizedSkills?: any;
  skills?: any[];
  tech?: any[];
  git?: string;
  startDate: string;
  endDate: string | null;
  activityId: number | number[] | null;
  githubLanguages?: Array<{ name: string; weight: number; }>;
}

// Cache for GitHub-enhanced projects to avoid repeated API calls
const githubEnhancedProjectsCache = new Map<string, EnhancedProject[]>();
const githubLanguageCache = new Map<string, Array<{ name: string; weight: number; }>>();

/**
 * Enhance a single project with GitHub language data if it has a GitHub link
 */
async function enhanceProjectWithGitHub(project: any): Promise<EnhancedProject> {
  const enhancedProject: EnhancedProject = { ...project };
  
  // Only enhance projects that have GitHub links and could benefit from language enhancement
  if (isOpenSourceGitHubRepo(project.git)) {
    const cacheKey = project.git;
    
    // Check cache first
    if (githubLanguageCache.has(cacheKey)) {
      enhancedProject.githubLanguages = githubLanguageCache.get(cacheKey);
    } else {
      try {
        // Fetch GitHub languages
        const githubLanguages = await fetchGitHubLanguages(project.git);
        const formattedLanguages = githubLanguages.map(lang => ({
          name: lang.name,
          weight: lang.weight
        }));
        
        // Cache the result
        githubLanguageCache.set(cacheKey, formattedLanguages);
        enhancedProject.githubLanguages = formattedLanguages;
      } catch (error) {
        console.warn(`Failed to enhance ${project.title} with GitHub data:`, error);
        enhancedProject.githubLanguages = [];
      }
    }
  }
  
  return enhancedProject;
}

/**
 * Get normalized skills for a project, with GitHub languages replacing static language skills
 */
function getNormalizedSkillsWithGitHub(project: EnhancedProject) {
  // Start with normal skill normalization
  const normalizedSkills = normalizeSkillFormat(project);
  
  // If we have GitHub languages, replace the language category
  if (project.githubLanguages && project.githubLanguages.length > 0) {
    normalizedSkills.language = project.githubLanguages;
  }
  
  return normalizedSkills;
}

/**
 * Calculate professional skills with GitHub language enhancement
 * This version fetches GitHub languages for projects that have GitHub links
 */
export async function calculateGitHubEnhancedProfessionalSkills(): Promise<ProfessionalSkillsByCategory> {
  // Get only professional projects (exclude private projects with activityId === null)
  const professionalProjects = projects.filter((project: any) => project.activityId !== null);
  
  const cacheKey = `github-enhanced-professional-${professionalProjects.length}-${activitiesData.length}`;
  
  // Check if we have cached enhanced projects
  if (githubEnhancedProjectsCache.has(cacheKey)) {
    const enhancedProjects = githubEnhancedProjectsCache.get(cacheKey)!;
    return calculateSkillsFromEnhancedProjects(enhancedProjects);
  }

  // Enhance only professional projects with GitHub data in parallel
  if (process.env.NODE_ENV === 'development') {
    console.log('Enhancing professional skills with GitHub language data...');
  }
  const enhancementPromises = professionalProjects.map(project => enhanceProjectWithGitHub(project));
  const enhancedProjects = await Promise.all(enhancementPromises);
  
  // Cache the enhanced projects
  githubEnhancedProjectsCache.set(cacheKey, enhancedProjects);
  
  return calculateSkillsFromEnhancedProjects(enhancedProjects);
}

/**
 * Calculate skills from enhanced projects (projects with GitHub data)
 */
function calculateSkillsFromEnhancedProjects(enhancedProjects: EnhancedProject[]): ProfessionalSkillsByCategory {
  const skillMaps = createSkillMaps();

  // Filter to only professional projects (exclude private projects)
  const professionalEnhancedProjects = enhancedProjects.filter(project => project.activityId !== null);

  // Calculate total duration of professional projects for normalization
  const totalProfessionalProjectMonths = professionalEnhancedProjects.reduce((total, project) => {
    const attributedDuration = calculateProfessionalAttributedProjectDuration(project);
    return total + attributedDuration;
  }, 0);

  // Process only professional enhanced projects
  professionalEnhancedProjects.forEach((project) => {
    // Handle multi-activity projects by splitting duration equally for professional overview
    const attributedDuration = calculateProfessionalAttributedProjectDuration(project);
    
    // Get normalized skill format with GitHub language enhancement
    const normalizedSkills = getNormalizedSkillsWithGitHub(project);
    
    // Process all skill categories
    Object.entries(normalizedSkills).forEach(([category, skills]) => {
      const categoryKey = category as keyof SkillMaps;
      skills.forEach((skill: any) => {
        if (!skill.name || typeof skill.weight !== 'number') return;
        
        addSkillToMap(skillMaps, categoryKey, skill.name, attributedDuration, skill.weight);
      });
    });
  });

  // Calculate normalized skill levels (0-100) for each category
  const skillsByCategory = normalizeSkillMaps(skillMaps, totalProfessionalProjectMonths);
  
  return skillsByCategory;
}

/**
 * Get professional skills with option for GitHub enhancement
 * Falls back to static calculation if GitHub enhancement fails
 */
export async function getProfessionalSkillsWithGitHubFallback(): Promise<ProfessionalSkillsByCategory> {
  try {
    return await calculateGitHubEnhancedProfessionalSkills();
  } catch (error) {
    console.warn('GitHub-enhanced professional skills calculation failed, falling back to static calculation:', error);
    // Import the static calculation as fallback
    const { calculateProfessionalSkills } = await import('../calculators/professionalSkillsUtils');
    return calculateProfessionalSkills();
  }
}

/**
 * Clear GitHub language cache (useful for development/testing)
 */
export function clearGitHubLanguageCache(): void {
  githubLanguageCache.clear();
  githubEnhancedProjectsCache.clear();
}
